import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import './BusinessIntelligence.css';

const BusinessIntelligence = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const [restaurants, setRestaurants] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState({ id: '', type: '' });
  const [filter, setFilter] = useState('last30days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [businessMetrics, setBusinessMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  useEffect(() => {
    fetchRestaurants();
    fetchStores();
    fetchBusinessMetrics();
  }, []);

  useEffect(() => {
    fetchBusinessMetrics();
  }, [filter, startDate, endDate]);

  useEffect(() => {
    if (selectedEntity.id && selectedEntity.type) {
      fetchAnalytics();
    }
  }, [selectedEntity, filter, startDate, endDate]);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/restaurants`);
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchBusinessMetrics = async () => {
    setLoadingMetrics(true);
    try {
      const params = {
        filter: filter !== 'custom' ? filter : undefined,
        startDate: filter === 'custom' ? startDate : undefined,
        endDate: filter === 'custom' ? endDate : undefined
      };
      
      const response = await axios.get(`${API_URL}/admin/business-intelligence`, { params });
      setBusinessMetrics(response.data);
    } catch (error) {
      console.error('Error fetching business metrics:', error);
    } finally {
      setLoadingMetrics(false);
    }
  };

  const fetchAnalytics = async () => {
    if (!selectedEntity.id || !selectedEntity.type) return;
    
    setLoading(true);
    try {
      const params = {
        restaurantId: selectedEntity.id,
        restaurantType: selectedEntity.type,
        filter: filter !== 'custom' ? filter : undefined,
        startDate: filter === 'custom' ? startDate : undefined,
        endDate: filter === 'custom' ? endDate : undefined
      };
      
      const response = await axios.get(`${API_URL}/admin/analytics`, { params });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      showError('Error loading analytics data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (newFilter !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const formatCurrency = (amount) => {
    return `€${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <div className="business-intelligence">
      <div className="bi-header">
        <h2>{t('admin.businessIntelligence')}</h2>
      </div>

      <div className="bi-filters">
        <div className="filter-group">
          <label>{t('admin.selectRestaurantStore')}</label>
          <div className="entity-selector">
            <select
              value={selectedEntity.type === 'Restaurant' ? selectedEntity.id : ''}
              onChange={(e) => {
                if (e.target.value) {
                  setSelectedEntity({ id: e.target.value, type: 'Restaurant' });
                } else {
                  setSelectedEntity({ id: '', type: '' });
                }
              }}
            >
              <option value="">{t('admin.selectRestaurant')}</option>
              {restaurants.map(restaurant => (
                <option key={restaurant._id} value={restaurant._id}>
                  {restaurant.name}
                </option>
              ))}
            </select>
            <select
              value={selectedEntity.type === 'Store' ? selectedEntity.id : ''}
              onChange={(e) => {
                if (e.target.value) {
                  setSelectedEntity({ id: e.target.value, type: 'Store' });
                } else {
                  setSelectedEntity({ id: '', type: '' });
                }
              }}
            >
              <option value="">{t('admin.selectStore')}</option>
              {stores.map(store => (
                <option key={store._id} value={store._id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-group">
          <label>{t('admin.timePeriod')}</label>
          <div className="time-filters">
            <button
              className={filter === 'today' ? 'active' : ''}
              onClick={() => handleFilterChange('today')}
            >
              {t('admin.today')}
            </button>
            <button
              className={filter === 'yesterday' ? 'active' : ''}
              onClick={() => handleFilterChange('yesterday')}
            >
              {t('admin.yesterday')}
            </button>
            <button
              className={filter === 'last7days' ? 'active' : ''}
              onClick={() => handleFilterChange('last7days')}
            >
              {t('admin.last7Days')}
            </button>
            <button
              className={filter === 'last30days' ? 'active' : ''}
              onClick={() => handleFilterChange('last30days')}
            >
              {t('admin.last30Days')}
            </button>
            <button
              className={filter === 'custom' ? 'active' : ''}
              onClick={() => handleFilterChange('custom')}
            >
              {t('admin.customRange')}
            </button>
          </div>
        </div>

        {filter === 'custom' && (
          <div className="filter-group">
            <label>{t('admin.dateRange')}</label>
            <div className="date-range">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>{t('admin.to')}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
          </div>
        )}
      </div>

      {/* Platform-wide Business Metrics */}
      <div className="business-metrics-section">
        <h3>{t('admin.platformBusinessMetrics')}</h3>
        {loadingMetrics && (
          <div className="loading-spinner">{t('admin.loadingMetrics')}</div>
        )}
        {!loadingMetrics && businessMetrics && (
          <div className="business-metrics-grid">
            <div className="metric-card highlight">
              <h4>{t('admin.totalDeliveryFeesCollected')}</h4>
              <p className="metric-value">{formatCurrency(businessMetrics.totalDeliveryFees)}</p>
              <span className="metric-note">{t('admin.allDeliveryFees')}</span>
            </div>
            <div className="metric-card highlight">
              <h4>{t('admin.totalDeliveryRateCuts')}</h4>
              <p className="metric-value">{formatCurrency(businessMetrics.totalDeliveryRateCuts)}</p>
              <span className="metric-note">
                {businessMetrics.businessConfig?.deliveryRate}% {t('admin.percentOfOrdersWithDrivers')}
              </span>
            </div>
            <div className="metric-card highlight">
              <h4>{t('admin.totalRatesNoDrivers')}</h4>
              <p className="metric-value">{formatCurrency(businessMetrics.totalRatesWithoutDrivers)}</p>
              <span className="metric-note">
                {businessMetrics.businessConfig?.orderRateWithoutDrivers}% {t('admin.percentOfOrdersWithoutDrivers')}
              </span>
            </div>
            <div className="metric-card highlight">
              <h4>{t('admin.totalTaxesCalculated')}</h4>
              <p className="metric-value">{formatCurrency(businessMetrics.totalTaxes)}</p>
              <span className="metric-note">{t('admin.allTaxAmounts')}</span>
            </div>
            <div className="metric-card highlight">
              <h4>{t('admin.totalServiceFeesCollected')}</h4>
              <p className="metric-value">{formatCurrency(businessMetrics.totalServiceFees)}</p>
              <span className="metric-note">
                {businessMetrics.businessConfig?.serviceFeePercentage || 0}% {t('admin.percentOfOrderValue')}
              </span>
            </div>
            <div className="metric-card highlight">
              <h4>{t('admin.totalDriverPayments')}</h4>
              <p className="metric-value">{formatCurrency(businessMetrics.totalDriverPayments)}</p>
              <span className="metric-note">{t('admin.amountToBePaidToDrivers')}</span>
            </div>
            <div className="metric-card">
              <h4>{t('admin.dateRange')}</h4>
              <p className="metric-value small">
                {formatDate(businessMetrics.dateRange.start)} - {formatDate(businessMetrics.dateRange.end)}
              </p>
            </div>
            <div className="metric-card">
              <h4>{t('admin.businessConfig')}</h4>
              <div className="config-details">
                <p><strong>{t('admin.deliveryRange')}:</strong> {businessMetrics.businessConfig?.deliveryRange} km</p>
                <p><strong>{t('admin.deliveryRate')}:</strong> {businessMetrics.businessConfig?.deliveryRate}%</p>
                <p><strong>{t('admin.orderRateNoDrivers')}:</strong> {businessMetrics.businessConfig?.orderRateWithoutDrivers}%</p>
                <p><strong>{t('admin.serviceFee')}:</strong> {businessMetrics.businessConfig?.serviceFeePercentage || 0}%</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-spinner">{t('admin.loadingAnalytics')}</div>
      )}

      {!loading && analytics && selectedEntity.id && (
        <>
          <div className="analytics-summary">
            <div className="summary-card">
              <h3>{t('admin.totalOrders')}</h3>
              <p className="summary-value">{analytics.totalOrders}</p>
            </div>
            <div className="summary-card">
              <h3>{t('admin.orderTotalAmount')}</h3>
              <p className="summary-value">{formatCurrency(analytics.orderTotalAmount)}</p>
            </div>
            <div className="summary-card">
              <h3>{t('admin.salesTotalAmount')}</h3>
              <p className="summary-value highlight">{formatCurrency(analytics.salesTotalAmount)}</p>
            </div>
            <div className="summary-card">
              <h3>{t('admin.dateRange')}</h3>
              <p className="summary-value small">
                {formatDate(analytics.dateRange.start)} - {formatDate(analytics.dateRange.end)}
              </p>
            </div>
          </div>

          <div className="analytics-details">
            <div className="detail-section">
              <h3>{t('admin.performanceMetrics')}</h3>
              <div className="metrics-grid">
                <div className="metric-card">
                  <h4>{t('admin.avgTimeToAcceptOrder')}</h4>
                  <p className="metric-value">{formatTime(analytics.avgTimeToAccept)}</p>
                  <span className="metric-note">
                    {t('admin.basedOnOrders')} {analytics.ordersWithTimeData.timeToAccept} {t('admin.orders')}
                  </span>
                </div>
                <div className="metric-card">
                  <h4>{t('admin.avgTimeToReadyForPickup')}</h4>
                  <p className="metric-value">{formatTime(analytics.avgTimeToReady)}</p>
                  <span className="metric-note">
                    {t('admin.basedOnOrders')} {analytics.ordersWithTimeData.timeToReady} {t('admin.orders')}
                  </span>
                </div>
                {analytics.customerSatisfaction !== null && (
                  <div className="metric-card">
                    <h4>{t('admin.customerSatisfaction')}</h4>
                    <p className="metric-value">
                      {analytics.customerSatisfaction.toFixed(1)} / 5.0
                    </p>
                    <div className="rating-stars">
                      {[1, 2, 3, 4, 5].map(star => (
                        <span
                          key={star}
                          className={star <= analytics.customerSatisfaction ? 'filled' : ''}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="detail-section">
              <h3>{t('admin.topSoldProducts')}</h3>
              {analytics.topSoldProducts.length > 0 ? (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>{t('admin.productName')}</th>
                        <th>{t('admin.quantitySold')}</th>
                        <th>{t('admin.revenue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topSoldProducts.map((product, index) => (
                        <tr key={index}>
                          <td>{product.name}</td>
                          <td>{product.quantity}</td>
                          <td>{formatCurrency(product.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="no-data">{t('admin.noProductSalesData')}</p>
              )}
            </div>

            <div className="detail-section">
              <h3>{t('admin.topSoldCategories')}</h3>
              {analytics.topSoldCategories.length > 0 ? (
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>{t('admin.category')}</th>
                        <th>{t('admin.quantitySold')}</th>
                        <th>{t('admin.revenue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topSoldCategories.map((category, index) => (
                        <tr key={index}>
                          <td>{category.category}</td>
                          <td>{category.quantity}</td>
                          <td>{formatCurrency(category.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="no-data">{t('admin.noCategorySalesData')}</p>
              )}
            </div>
          </div>
        </>
      )}

      {!loading && !analytics && selectedEntity.id && (
        <div className="no-data-message">
          <p>{t('admin.noAnalyticsData')}</p>
        </div>
      )}

      {!loading && !selectedEntity.id && (
        <div className="no-selection-message">
          <p>{t('admin.pleaseSelectRestaurantStore')}</p>
        </div>
      )}
    </div>
  );
};

export default BusinessIntelligence;
